import { useState, useEffect, useCallback, useMemo } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// ── Painpoint Research — niches list, run trigger, ranked results ────────────

interface RunCounts { painpoints: number; items: number; clusters: number }
interface RunSummary {
  id: number;
  status: 'running' | 'done' | 'error';
  phase: string | null;
  started_at: string;
  finished_at: string | null;
  engine_version: string | null;
  sources_active: string[];
  error_message: string | null;
  lookback_days: number | null;
  counts: RunCounts;
}
interface Niche {
  id: number;
  name: string;
  seed_query: string;
  run_count: number;
  latest_run: RunSummary | null;
}
interface Painpoint {
  id: number;
  statement: string;
  frequency: number;
  source_diversity: number;
  intent_score: number;
  sentiment: number;
  engagement_norm: number;
  composite_score: number;
  low_confidence: number;
  extracted_by: string;
  evidence_count: number;
  validation_verdict: string | null;
}

const PHASES = ['ingest', 'parse', 'normalize', 'extract', 'score', 'persist'];

async function getJSON<T>(url: string): Promise<T> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}
async function postJSON<T>(url: string, body?: unknown): Promise<T> {
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
  return r.json();
}

type SortKey = 'composite_score' | 'frequency' | 'intent_score' | 'engagement_norm' | 'source_diversity';

function PhaseBar({ phase }: { phase: string | null }) {
  const idx = phase ? PHASES.indexOf(phase) : -1;
  return (
    <div className="flex items-center gap-1.5">
      {PHASES.map((p, i) => (
        <div key={p} className="flex items-center gap-1.5">
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${
              i < idx ? 'bg-green-100 text-green-700'
              : i === idx ? 'bg-blue-100 text-blue-700 animate-pulse'
              : 'bg-gray-100 text-gray-400'
            }`}
          >
            {p}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function Painpoints() {
  const navigate = useNavigate();
  const { nicheId } = useParams();
  const [niches, setNiches] = useState<Niche[]>([]);
  const [selected, setSelected] = useState<number | null>(nicheId ? Number(nicheId) : null);
  const [newName, setNewName] = useState('');
  const [newSeed, setNewSeed] = useState('');
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [activeRun, setActiveRun] = useState<RunSummary | null>(null);
  const [painpoints, setPainpoints] = useState<Painpoint[]>([]);
  const [resultRun, setResultRun] = useState<RunSummary | null>(null);
  const [history, setHistory] = useState<RunSummary[]>([]);
  const [viewRunId, setViewRunId] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('composite_score');

  const loadNiches = useCallback(async () => {
    try {
      setNiches(await getJSON<Niche[]>('/api/painpoints/niches'));
    } catch (e) { setErr((e as Error).message); }
  }, []);

  useEffect(() => { loadNiches(); }, [loadNiches]);
  useEffect(() => { if (nicheId) setSelected(Number(nicheId)); }, [nicheId]);

  const loadResults = useCallback(async (nid: number, runId?: number | null) => {
    const q = runId ? `?run_id=${runId}` : '';
    const data = await getJSON<{ run: RunSummary | null; painpoints: Painpoint[] }>(
      `/api/painpoints/niches/${nid}/results${q}`,
    );
    setPainpoints(data.painpoints);
    setResultRun(data.run);
    setHistory(await getJSON<RunSummary[]>(`/api/painpoints/niches/${nid}/runs`));
  }, []);

  useEffect(() => {
    if (selected) loadResults(selected, viewRunId).catch((e) => setErr((e as Error).message));
  }, [selected, viewRunId, loadResults]);

  // Poll an in-flight run's status until it finishes, then refresh results.
  useEffect(() => {
    if (!activeRun || activeRun.status !== 'running') return;
    const t = setInterval(async () => {
      try {
        const r = await getJSON<RunSummary>(`/api/painpoints/runs/${activeRun.id}`);
        setActiveRun(r);
        if (r.status !== 'running') {
          clearInterval(t);
          if (selected) { setViewRunId(null); await loadResults(selected, null); }
          loadNiches();
        }
      } catch { /* keep polling */ }
    }, 800);
    return () => clearInterval(t);
  }, [activeRun, selected, loadResults, loadNiches]);

  async function addNiche(e: FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    setAdding(true);
    setErr(null);
    try {
      const n = await postJSON<Niche>('/api/painpoints/niches', {
        name: newName.trim(), seed_query: newSeed.trim() || undefined,
      });
      setNewName(''); setNewSeed('');
      await loadNiches();
      setSelected(n.id);
    } catch (e) { setErr((e as Error).message); }
    finally { setAdding(false); }
  }

  async function runResearch(nid: number) {
    setErr(null);
    try {
      const { run_id } = await postJSON<{ run_id: number }>(`/api/painpoints/niches/${nid}/run`);
      const r = await getJSON<RunSummary>(`/api/painpoints/runs/${run_id}`);
      setActiveRun(r);
    } catch (e) { setErr((e as Error).message); }
  }

  const selectedNiche = niches.find((n) => n.id === selected) || null;
  const sorted = useMemo(
    () => [...painpoints].sort((a, b) => (b[sortKey] as number) - (a[sortKey] as number)),
    [painpoints, sortKey],
  );

  return (
    <div className="max-w-7xl mx-auto">
      <h2 className="text-xl sm:text-2xl font-bold mb-6 text-gray-900">Painpoint Research 🎯</h2>
      <p className="text-sm text-gray-500 mb-6 max-w-3xl">
        Harvests last-30-days customer discussion for each niche, extracts painpoints, and ranks
        them so <strong>frequency</strong> and <strong>purchase intent</strong> outweigh raw
        engagement. Each run is an immutable snapshot.
      </p>

      {err && <div className="bg-red-50 text-red-700 rounded-lg p-3 mb-4 text-sm">{err}</div>}

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
        {/* Niches sidebar */}
        <div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Add a niche</h3>
            <form onSubmit={addNiche} className="space-y-2">
              <input
                value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. wireless earbuds for small ears"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
              <input
                value={newSeed} onChange={(e) => setNewSeed(e.target.value)}
                placeholder="seed query (optional)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
              <button
                type="submit" disabled={adding || !newName.trim()}
                className="w-full px-3 py-2 bg-gray-900 text-white rounded-md text-sm font-medium disabled:opacity-40"
              >
                {adding ? 'Adding…' : 'Add niche'}
              </button>
            </form>
          </div>

          <div className="space-y-1">
            {niches.map((n) => (
              <button
                key={n.id}
                onClick={() => { setSelected(n.id); setViewRunId(null); navigate(`/painpoints/${n.id}`); }}
                className={`w-full text-left px-3 py-2.5 rounded-md text-sm border ${
                  selected === n.id ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium truncate">{n.name}</div>
                <div className={`text-xs ${selected === n.id ? 'text-gray-300' : 'text-gray-400'}`}>
                  {n.run_count} run{n.run_count === 1 ? '' : 's'}
                  {n.latest_run?.status === 'done' && ` · ${n.latest_run.counts.painpoints} painpoints`}
                  {n.latest_run?.status === 'running' && ' · running…'}
                  {n.latest_run?.status === 'error' && ' · error'}
                </div>
              </button>
            ))}
            {niches.length === 0 && <div className="text-sm text-gray-400 px-3 py-2">No niches yet.</div>}
          </div>
        </div>

        {/* Results panel */}
        <div>
          {!selectedNiche && (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-400 text-sm">
              Select a niche to view ranked painpoints, or add one to begin.
            </div>
          )}

          {selectedNiche && (
            <div>
              <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedNiche.name}</h3>
                  {resultRun && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      Run #{resultRun.id} · {resultRun.started_at} · sources: {resultRun.sources_active.join(', ') || '—'}
                      {resultRun.lookback_days != null && ` · window: ${resultRun.lookback_days}d`}
                      {resultRun.engine_version && ` · engine v${resultRun.engine_version}`}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {history.length > 0 && (
                    <select
                      value={viewRunId ?? ''}
                      onChange={(e) => setViewRunId(e.target.value ? Number(e.target.value) : null)}
                      className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
                    >
                      <option value="">Latest run</option>
                      {history.map((r) => (
                        <option key={r.id} value={r.id}>
                          #{r.id} · {r.started_at.slice(0, 16).replace('T', ' ')} · {r.status}
                        </option>
                      ))}
                    </select>
                  )}
                  <button
                    onClick={() => runResearch(selectedNiche.id)}
                    disabled={activeRun?.status === 'running'}
                    className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm font-medium disabled:opacity-40"
                  >
                    {activeRun?.status === 'running' ? 'Running…' : 'Run research'}
                  </button>
                </div>
              </div>

              {activeRun?.status === 'running' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-3">
                  <div className="w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                  <PhaseBar phase={activeRun.phase} />
                </div>
              )}
              {activeRun?.status === 'error' && (
                <div className="bg-red-50 text-red-700 rounded-lg p-3 mb-4 text-sm">
                  Run failed: {activeRun.error_message}
                </div>
              )}

              {sorted.length === 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-400 text-sm">
                  {resultRun ? 'No painpoints in this run.' : 'No runs yet — click “Run research”.'}
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
                        <th className="px-4 py-3">Painpoint</th>
                        {([
                          ['composite_score', 'Score'],
                          ['frequency', 'Freq'],
                          ['intent_score', 'Intent'],
                          ['engagement_norm', 'Engmt'],
                          ['source_diversity', 'Sources'],
                        ] as [SortKey, string][]).map(([k, label]) => (
                          <th
                            key={k}
                            onClick={() => setSortKey(k)}
                            className={`px-3 py-3 cursor-pointer whitespace-nowrap select-none ${
                              sortKey === k ? 'text-gray-900' : 'hover:text-gray-700'
                            }`}
                          >
                            {label}{sortKey === k ? ' ↓' : ''}
                          </th>
                        ))}
                        <th className="px-3 py-3">Confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((p) => (
                        <tr
                          key={p.id}
                          onClick={() => navigate(`/painpoints/detail/${p.id}`)}
                          className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                        >
                          <td className="px-4 py-3 max-w-md">
                            <div className="text-gray-900">{p.statement}</div>
                            <div className="text-xs text-gray-400 mt-0.5">
                              {p.evidence_count} item{p.evidence_count === 1 ? '' : 's'} · {p.extracted_by}
                              {p.validation_verdict && ` · validated: ${p.validation_verdict}`}
                            </div>
                          </td>
                          <td className="px-3 py-3 font-semibold text-gray-900">{p.composite_score.toFixed(1)}</td>
                          <td className="px-3 py-3 text-gray-600">{p.frequency}</td>
                          <td className="px-3 py-3 text-gray-600">{p.intent_score.toFixed(2)}</td>
                          <td className="px-3 py-3 text-gray-600">{(p.engagement_norm * 100).toFixed(0)}%</td>
                          <td className="px-3 py-3 text-gray-600">{p.source_diversity}</td>
                          <td className="px-3 py-3">
                            {p.low_confidence ? (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">⚠ low</span>
                            ) : (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">ok</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
